import React, { useRef, useEffect, useState } from 'react';
import { ICareerCardProps, ICareerTag } from '../CareerCard';
import Icon from 'tbk-components/src/components/Icon';
import cn from 'classnames';
import Button from 'tbk-components/src/components/Button';
import DashboardCardModal, {
	IDashboardCardModalI18n,
} from './DashboardCardModal';
import { AnimationOnScroll } from 'react-animation-on-scroll';

export interface IDashboardCardI18n {
	fullApplicationSubmitted: string;
	quickApplicationSubmitted: string;
	notificationFormSubmitted: string;
	iAlreadyCompletedMyApplication: string;
	alreadyCompletedTooltip: string;
	completeApplication: string;
	thisPostingNeedsYourAttention: string;
	dismissWarning: string;
}

export interface IDashboardCareerCardProps extends ICareerCardProps {
	needsAttention?: boolean;
	fullApplicationSubmitted: boolean;
	quickApplicationSubmitted: boolean;
	notificationFormSubmitted: boolean;
	hasBeenDismissed: boolean;
	sourceATS: {
		label: string;
		link: string;
	};
	isDeskflow?: boolean;
}

export interface IDashboardCardProps extends ICareerCardProps {
	i18n?: IDashboardCardI18n;
	modalI18n?: IDashboardCardModalI18n;
	card?: IDashboardCareerCardProps;
	onManuallyCompleted: (ID: number) => void;
	onDismissed: (ID: number) => void;
	animationDelay?: number;
}

export const DashboardCardTags: React.FC<{
	tags: ICareerTag[];
	classNames?: string[];
}> = ({ tags, classNames }) => {
	return (
		<ul className={cn('calian-career-card__tags list-inline', classNames)}>
			{tags.map(({ label, taxonomy, slug }, index) => (
				<li
					key={index}
					className="calian-career-card__tag list-inline-item"
				>
					<span className="calian-career-card__tag--btn btn btn-sm fs-8">
						{'status' === taxonomy && (
							<span
								className={cn(
									'closed' === slug
										? 'text-warning'
										: 'text-success',
									'fs-8 me-2',
								)}
							>
								<Icon
									name={
										'closed' === slug ? 'close' : 'success'
									}
								/>
							</span>
						)}
						{label}
					</span>
				</li>
			))}
		</ul>
	);
};

export const DashboardCardStatus: React.FC<{
	card: IDashboardCareerCardProps;
	needsAttention: boolean;
	i18n: {
		fullApplicationSubmitted: string;
		quickApplicationSubmitted: string;
		notificationFormSubmitted: string;
	};
}> = ({ card, needsAttention, i18n }) => {
	return (
		<>
			<Icon
				name={
					needsAttention
						? 'check-circle text-accent-1'
						: 'success text-accent-2'
				}
			/>
			<span className="dashboard-status-text mx-1 fw-normal">
				{card.fullApplicationSubmitted
					? i18n.fullApplicationSubmitted
					: card.quickApplicationSubmitted
					? i18n.quickApplicationSubmitted
					: card.notificationFormSubmitted
					? i18n.notificationFormSubmitted
					: ''}
			</span>
		</>
	);
};

const DashboardCard: React.FC<IDashboardCardProps> = ({
	i18n = {
		fullApplicationSubmitted: 'Full application submitted',
		quickApplicationSubmitted: 'Quick application submitted',
		notificationFormSubmitted: 'Notification form submitted',
		iAlreadyCompletedMyApplication: 'I already completed my application',
		completeApplication: 'Complete application',
		thisPostingNeedsYourAttention: 'This posting needs your attention',
		alreadyCompletedTooltip:
			'If you have already completed this application, you can mark it as complete here to remove it from the applications needing attention',
		dismissWarning: 'dismiss',
	} as IDashboardCardI18n,
	modalI18n,
	card,
	onManuallyCompleted = (ID: number) => {},
	onDismissed = (ID: number) => {},
	animationDelay = 0,
}) => {
	const isClosed = card.careerTags?.find(
		(tag) => tag.taxonomy === 'status' && tag.slug === 'closed',
	)
		? true
		: false;
	const needsAttention =
		!card.fullApplicationSubmitted && !card.hasBeenDismissed && !isClosed;
	const [showModal, setShowModal] = useState<boolean>(false);
	const tooltipRef = useRef();
	useEffect(() => {
		if (!tooltipRef?.current) {
			return;
		}
		import('bootstrap/js/dist/tooltip').then(({ default: Tooltip }) => {
			new Tooltip(tooltipRef.current);
		});
	}, [tooltipRef?.current]);

	return (
		<div
			className={cn('dashboard-card col-12 mb-4', {
				'col-md-6': !needsAttention,
			})}
		>
			<AnimationOnScroll
				animateIn="fadeInUp"
				animateOnce={true}
				delay={animationDelay}
				offset={100}
			>
				{needsAttention && (
					<div className="mb-2 px-1 px-sm-3 px-lg-5 fs-7 fs-lg-6 fw-normal">
						<span className="me-2">
							<i className="icon icon-alert fs-7 fs-6 text-accent-1"></i>
						</span>
						{i18n.thisPostingNeedsYourAttention}
						<Button
							label={i18n.dismissWarning}
							size="sm"
							className="btn btn-link link-accent-1-darker border-0 bg-transparent text-decoration-none text-capitalize ms-1 fs-7 fs-lg-6 p-0 pb-1"
							onClick={() => onDismissed(card.ID)}
						/>
					</div>
				)}

				<div
					className={cn(
						'card pe-2 pe-md-3 p-3 py-lg-4 d-flex flex-column rounded-4 rounded-sm-5 ',
						{
							'ps-lg-5 pe-lg-4 attention-card border-career-dashboard-card-yellow-dark bg-career-dashboard-card-yellow-light':
								needsAttention,
							'px-lg-5': !needsAttention,
						},
					)}
					onClick={() => setShowModal(true)}
				>
					<div className="d-block mb-auto">
						<div className="d-flex">
							<div className="w-60 w-lg-100 flex-grow-1 pe-2">
								<button
									className="text-start p-0 bg-transparent border-0 p-0"
									onClick={() => setShowModal(true)}
								>
									<h3 className="h5 mb-1 text-primary-hover">
										{card.careerTitle}
									</h3>
								</button>
								{card.careerTags?.length && (
									<DashboardCardTags tags={card.careerTags} />
								)}
							</div>
							<div className="d-none d-lg-block">
								{needsAttention ? (
									<Button
										label={i18n.completeApplication}
										classNames={[
											'py-2',
											'px-3',
											'fs-8',
											'min-width-165px',
										]}
										onClick={() => setShowModal(true)}
									/>
								) : (
									<Button
										aria-label={
											i18n.iAlreadyCompletedMyApplication
										}
										className="btn btn-link text-decoration-none bg-transparent border-0 p-0 fs-5"
										onClick={() => setShowModal(true)}
										icon="arrow-right"
									/>
								)}
							</div>
							<div className="w-40 d-flex flex-row-reverse d-lg-none fs-7 mt-1">
								<DashboardCardStatus
									card={card}
									needsAttention={needsAttention}
									i18n={i18n}
								/>
							</div>
						</div>
					</div>
					<div className="d-block mt-auto">
						{needsAttention && (
							<div className="my-3 d-lg-none">
								<Button
									label={i18n.completeApplication}
									classNames={[
										'py-2',
										'px-3',
										'fs-8',
										'min-width-165px',
									]}
									onClick={() => setShowModal(true)}
								/>
							</div>
						)}
						<div className="d-flex mt-auto">
							<div className="d-none d-lg-block fs-7 mt-1">
								<DashboardCardStatus
									card={card}
									needsAttention={needsAttention}
									i18n={i18n}
								/>
							</div>
							{needsAttention && (
								<div className="attention-checkbox mr-auto ms-lg-auto text-start text-lg-end fs-7">
									<label
										className="d-flex flex-row justify-content-center align-items-center"
										htmlFor={`attention-${card.ID}-checkbox`}
									>
										<input
											className="form-check-input"
											type="checkbox"
											onChange={(e) =>
												e.target.checked &&
												onManuallyCompleted(card.ID)
											}
											id={`attention-${card.ID}-checkbox`}
										/>
										{i18n.iAlreadyCompletedMyApplication}
										<button
											ref={tooltipRef}
											type="button"
											className="bg-transparent border-0 lh-1 ms-1"
											data-bs-toggle="tooltip"
											data-bs-placement="top"
											title={i18n.alreadyCompletedTooltip}
										>
											<Icon name="question fs-6 text-mid-grey" />
										</button>
									</label>
								</div>
							)}
						</div>
					</div>
				</div>
				{showModal && (
					<DashboardCardModal
						onCancel={() => setShowModal(false)}
						onCompletedApplication={(ID: number) => {
							setShowModal(false);
							onManuallyCompleted(ID);
						}}
						i18n={modalI18n}
						card={card}
					/>
				)}
			</AnimationOnScroll>
		</div>
	);
};

export default DashboardCard;
